import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "selectedItems", "searchResults", "searchInput"]
  static values = { 
    types: Array,
    placeholder: String,
    maxItems: Number
  }

  connect() {
    this.selectedItems = new Map() // Store id -> item data mapping
    this.loadSelectedItems()
    this.setupSearchInput()
  }

  disconnect() {
    this.selectedItems.clear()
  }

  loadSelectedItems() {
    const inputValue = this.inputTarget.value
    if (inputValue) {
      const entries = inputValue.split(',').map(entry => entry.trim()).filter(entry => entry)
      
      // Parse id:type format
      entries.forEach(entry => {
        if (entry.includes(':')) {
          const [id, type] = entry.split(':')
          this.selectedItems.set(entry, { id: id, name: `Loading...`, type: type, typeLabel: this.getTypeLabel(type) })
        } else {
          // Fallback for old format
          this.selectedItems.set(entry, { id: entry, name: `Loading...`, type: 'Unknown' })
        }
      })
      this.updateDisplay()
      // Fetch actual data for existing items
      this.loadExistingItemsData(entries)
    }
  }

  async loadExistingItemsData(entries) {
    try {
      // Extract just the IDs for the API call
      const ids = entries.map(entry => {
        if (entry.includes(':')) {
          return entry.split(':')[0]
        }
        return entry
      })
      
      // Load all existing items at once using the ids parameter
      const response = await fetch(`/backend/subjectable_search?ids=${encodeURIComponent(ids.join(','))}`)
      const data = await response.json()
      
      // Map the data back to the original entries with type information
      entries.forEach(entry => {
        const id = entry.includes(':') ? entry.split(':')[0] : entry
        const type = entry.includes(':') ? entry.split(':')[1] : 'Unknown'
        
        const item = data.find(item => item.id.toString() === id.toString())
        if (item) {
          this.selectedItems.set(entry, {
            id: item.id,
            name: item.display_name || item.name,
            type: type,
            typeLabel: this.getTypeLabel(type)
          })
        }
      })
      this.updateDisplay()
    } catch (error) {
      // Fallback to individual loading if batch loading fails
      const promises = entries.map(entry => this.fetchItemData(entry))
      const results = await Promise.all(promises)
      
      results.forEach(item => {
        if (item) {
          const key = `${item.id}:${item.type}`
          this.selectedItems.set(key, item)
        }
      })
      this.updateDisplay()
    }
  }

  async fetchItemData(entry) {
    try {
      const id = entry.includes(':') ? entry.split(':')[0] : entry
      const type = entry.includes(':') ? entry.split(':')[1] : 'Unknown'
      
      // Use the load_by_ids endpoint to fetch specific items by ID
      const response = await fetch(`/backend/subjectable_search?ids=${encodeURIComponent(id)}`)
      const data = await response.json()
      const item = data.find(item => item.id.toString() === id.toString())
      return item ? {
        id: item.id,
        name: item.display_name || item.name,
        type: type,
        typeLabel: this.getTypeLabel(type)
      } : null
    } catch (error) {
      console.error(`Error fetching item data for ${entry}:`, error)
      return null
    }
  }

  setupSearchInput() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.addEventListener('input', this.debounce(() => {
        this.performSearch()
      }, 300))
    }
  }

  performSearch() {
    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) {
      this.hideSearchResults()
      return
    }

    const types = this.typesValue || ['Contact', 'Customer', 'Requisition', 'Quote', 'Applicant', 'Profile', 'Talent']
    const promises = types.map(type => this.searchType(type, query))
    
    Promise.all(promises).then(results => {
      const allResults = results.flat()
      this.displaySearchResults(allResults)
    })
  }

  async searchType(type, query) {
    try {
      const response = await fetch(`/backend/subjectable_search?type=${type}&q=${encodeURIComponent(query)}`)
      const data = await response.json()
      return data.map(item => ({
        ...item,
        type: type,
        typeLabel: this.getTypeLabel(type)
      }))
    } catch (error) {
      console.error(`Error searching ${type}:`, error)
      return []
    }
  }

  displaySearchResults(results) {
    if (!this.hasSearchResultsTarget) return

    const html = results.map(result => `
      <div class="px-3 py-2 hover:bg-gray-100 cursor-pointer" 
           data-action="click->subjectable-multiselect#selectResult"
           data-subjectable-id="${result.id}"
           data-subjectable-type="${result.type}"
           data-subjectable-name="${result.display_name || result.name || 'Unknown'}">
        <div class="flex items-center">
          <span class="text-sm font-medium">${result.display_name || result.name || 'Unknown'}</span>
          <span class="ml-2 text-xs text-gray-500">${result.typeLabel || this.getTypeLabel(result.type)}</span>
        </div>
      </div>
    `).join('')

    this.searchResultsTarget.innerHTML = html
    this.searchResultsTarget.classList.remove('hidden')
  }

  hideSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add('hidden')
    }
  }

  selectResult(event) {
    const element = event.currentTarget
    const id = element.dataset.subjectableId
    const type = element.dataset.subjectableType
    const name = element.dataset.subjectableName

    if (this.maxItemsValue && this.selectedItems.size >= this.maxItemsValue) {
      alert(`Maximal ${this.maxItemsValue} Elemente erlaubt`)
      return
    }

    // Create the id:type key
    const key = `${id}:${type}`

    // Store the item data with proper fallbacks
    this.selectedItems.set(key, {
      id: id,
      name: name || 'Unknown',
      type: type || 'Unknown',
      typeLabel: this.getTypeLabel(type || 'Unknown')
    })
    
    this.updateDisplay()
    this.hideSearchResults()
    
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
    }
  }

  removeItem(event) {
    const id = event.currentTarget.dataset.id
    const type = event.currentTarget.dataset.type
    const key = type ? `${id}:${type}` : id
    this.selectedItems.delete(key)
    this.updateDisplay()
  }

  updateDisplay() {
    if (!this.hasSelectedItemsTarget) return

    const html = Array.from(this.selectedItems.values()).map(item => {
      const displayName = item.name || item.display_name || 'Unknown'
      const displayType = item.type || 'Unknown'
      return `
        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2 mb-2">
          <span class="mr-1">${this.getTypeIcon(displayType)}</span>
          ${displayName}
          <button type="button" 
                  class="ml-1 text-blue-600 hover:text-blue-800"
                  data-action="click->subjectable-multiselect#removeItem"
                  data-id="${item.id}"
                  data-type="${displayType}">
            ×
          </button>
        </span>
      `
    }).join('')

    this.selectedItemsTarget.innerHTML = html
    this.updateInputValue()
  }

  updateInputValue() {
    this.inputTarget.value = Array.from(this.selectedItems.keys()).join(',')
  }

  getTypeIcon(type) {
    const icons = {
      'Contact': '<i class="fas fa-user text-green-600"></i>',
      'Customer': '<i class="fas fa-building text-blue-600"></i>',
      'Requisition': '<i class="fas fa-briefcase text-purple-600"></i>',
      'Quote': '<i class="fas fa-file-invoice text-orange-600"></i>',
      'Applicant': '<i class="fas fa-id-card text-red-600"></i>',
      'Profile': '<i class="fas fa-user-circle text-indigo-600"></i>',
      'Talent': '<i class="fas fa-star text-yellow-600"></i>'
    }
    return icons[type] || '<i class="fas fa-database text-gray-600"></i>'
  }

  getTypeLabel(type) {
    const labels = {
      'Contact': 'Kontakt',
      'Customer': 'Kunde', 
      'Requisition': 'Stelle',
      'Quote': 'Angebot',
      'Applicant': 'Bewerber',
      'Profile': 'Profil',
      'Talent': 'Talent'
    }
    return labels[type] || type
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
} 